import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, CheckCircle2, Home as HomeIcon, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errText } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const rupees = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function TenantOnboard() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", joined_on: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useSeo({ title: "Tenant details form", noindex: true });

  useEffect(() => {
    api
      .get(`/public/onboard/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(errText(err)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/public/onboard/${token}`, form);
      setDone(true);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[620px] px-5 py-12" data-testid="tenant-onboard-page">
      <Link to="/" className="flex items-center gap-2 text-primary">
        <HomeIcon className="h-5 w-5" strokeWidth={2.2} />
        <span className="font-display text-[16px] tracking-tight">HOUSES7.COM</span>
      </Link>

      {error ? (
        <div className="mt-8 rounded-2xl border border-border p-8" data-testid="onboard-invalid">
          <p className="font-display text-xl">This link isn't valid any more</p>
          <p className="mt-2 text-sm text-muted-foreground">{error} Please ask the owner for a fresh link.</p>
        </div>
      ) : done ? (
        <div className="mt-8 rounded-2xl border border-border p-8" data-testid="onboard-success">
          <CheckCircle2 className="h-8 w-8 text-primary" />
          <p className="mt-3 font-display text-2xl">Thank you, {form.name.split(" ")[0]}!</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Your details have gone to the owner of {info?.stay_name}. They'll confirm your bed shortly.
          </p>
        </div>
      ) : !info ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mt-7 rounded-2xl border border-border p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {info.kind === "rental" ? "Rental unit" : "PG / co-living"}
            </p>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl">{info.stay_name}</h1>
            <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> {[info.locality, info.city].filter(Boolean).join(", ")}
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
              <span className="flex items-center gap-1.5">
                <BedDouble className="h-4 w-4 text-primary" />
                {[info.floor_label, info.room_label, info.bed_label].filter((x) => x && x !== "-").join(" · ")}
              </span>
              <span className="font-bold">{rupees(info.rent)} / month</span>
            </div>
          </div>

          {info.already_filled && (
            <p className="mt-4 rounded-lg bg-muted px-4 py-3 text-[13px] text-muted-foreground" data-testid="onboard-already">
              Details were already submitted for this bed. Sending again will replace them.
            </p>
          )}

          <form onSubmit={submit} className="mt-6 rounded-2xl border border-border p-6 space-y-4" data-testid="onboard-form">
            <div>
              <h2 className="font-display text-lg">Your details</h2>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Fill this once — the owner uses it for your rent receipts and reminders.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Full name</Label>
                <Input value={form.name} onChange={set("name")} required className="h-12 rounded-lg" data-testid="onboard-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Mobile number</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  required
                  minLength={10}
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                  className="h-12 rounded-lg"
                  data-testid="onboard-phone-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} className="h-12 rounded-lg" data-testid="onboard-email-input" />
                <p className="text-[11px] text-muted-foreground">Rent receipts and reminders come here.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Moving in on</Label>
                <Input type="date" value={form.joined_on} onChange={set("joined_on")} className="h-12 rounded-lg" data-testid="onboard-joined-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Anything the owner should know (optional)</Label>
              <Textarea value={form.notes} onChange={set("notes")} rows={3} className="rounded-lg" data-testid="onboard-notes-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-lg font-bold hover:brightness-95" data-testid="onboard-submit-btn">
              {busy ? "Sending…" : "Send my details"}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              No payment is collected here. Your details go only to this owner.
            </p>
          </form>
        </>
      )}
    </div>
  );
}
